/**
 * Dealer Directory analytics helpers (Dealer Directory M12).
 */
import { buildAttributionContext } from "../../analytics/attribution";
import { sanitizeAnalyticsPayload } from "../../analytics/sanitize";
import {
  DEALER_DIRECTORY_ANALYTICS_EVENTS,
  DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS,
  DEALER_DIRECTORY_EMPTY_TYPES,
  DEALER_DIRECTORY_SOURCE_PAGE,
} from "../../constants/dealerDirectoryAnalyticsConstants";
import {
  createDefaultDealerDirectoryFilterParams,
  parseDealerDirectoryParams,
} from "../../constants/dealerDirectoryFilterParamsConstants";
import { trackDealerProfileEvent } from "../../services/automapDealerPublicApi";
import { filterPublicDirectoryDealers } from "./dealerDirectoryVisibility";
import { mapDealerDirectoryErrorCategory } from "./dealerDirectoryAnalyticsSafety";

const BLOCKED_ITEM_FIELDS = new Set([
  "searchText",
  "warnings",
  "profileHref",
  "logoUrl",
  "initials",
  "contactAvailabilityLabel",
  "inventoryCountLabel",
  "lastInventoryUpdatedLabel",
  "verificationLabel",
  "locationLabel",
  "phone",
  "whatsapp",
  "email",
  "address",
]);

const BLOCKED_EXTRA_FIELDS = new Set([
  "internalReason",
  "moderationNote",
  "internalModerationReason",
  "exclusionReason",
  "rawDealer",
  "rawListing",
  "buyerEmail",
  "buyerPhone",
  "buyerWhatsapp",
]);

function omitBlockedAnalyticsFields(extra = {}) {
  const clean = { ...extra };
  BLOCKED_EXTRA_FIELDS.forEach((key) => {
    delete clean[key];
  });
  return clean;
}

export function buildDealerDirectoryAttributionContext(extra = {}) {
  const parsed =
    typeof window !== "undefined"
      ? parseDealerDirectoryParams(new URLSearchParams(window.location.search))
      : createDefaultDealerDirectoryFilterParams();

  return buildAttributionContext({
    sourcePage: DEALER_DIRECTORY_SOURCE_PAGE,
    sort: parsed.sort || null,
    activeFilterKeys: getActiveDealerDirectoryFilterKeys(parsed),
    activeFilterCount: getActiveDealerDirectoryFilterKeys(parsed).length,
    ...extra,
  });
}

export function getActiveDealerDirectoryFilterKeys(params = {}) {
  const parsed = parseDealerDirectoryParams(params);
  /** @type {string[]} */
  const keys = [];
  if (parsed.city) keys.push("city");
  if (parsed.neighborhood) keys.push("neighborhood");
  if (parsed.verifiedOnly) keys.push("verifiedOnly");
  if (parsed.hasInventory) keys.push("hasInventory");
  if (parsed.contactAvailable) keys.push("contactAvailable");
  return keys;
}

export function buildDealerDirectoryAnalyticsPayload({
  sourceComponent = DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.PAGE,
  sourceAction = null,
  visibleDealerCount = null,
  filteredDealerCount = null,
  verifiedDealerCount = null,
  withInventoryCount = null,
  contactAvailableCount = null,
  activeFilterCount = null,
  sort = null,
  filterParams = null,
  extra = {},
} = {}) {
  const parsed = filterParams ? parseDealerDirectoryParams(filterParams) : null;
  const activeFilterKeys = parsed ? getActiveDealerDirectoryFilterKeys(parsed) : extra.activeFilterKeys;

  return sanitizeAnalyticsPayload({
    sourcePage: DEALER_DIRECTORY_SOURCE_PAGE,
    sourceComponent,
    sourceAction,
    visibleDealerCount,
    filteredDealerCount,
    verifiedDealerCount,
    withInventoryCount,
    contactAvailableCount,
    activeFilterCount:
      activeFilterCount ??
      (Array.isArray(activeFilterKeys) ? activeFilterKeys.length : null),
    activeFilterKeys,
    sort: sort ?? parsed?.sort ?? null,
    attributionContext: buildDealerDirectoryAttributionContext({
      sourceComponent,
      sourceAction,
    }),
    ...omitBlockedAnalyticsFields(extra),
  });
}

/**
 * @param {import('./dealerDirectoryTypes').DealerDirectoryItem|null|undefined} item
 */
export function buildDealerDirectoryItemAnalyticsPayload(item, {
  sourceComponent = DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.CARD,
  sourceAction = null,
  position = null,
  extra = {},
} = {}) {
  if (!item) {
    return sanitizeAnalyticsPayload({
      sourcePage: DEALER_DIRECTORY_SOURCE_PAGE,
      sourceComponent,
      sourceAction,
      ...omitBlockedAnalyticsFields(extra),
    });
  }

  const payload = {
    dealerId: item.id || null,
    dealerSlug: item.slug || null,
    dealerName: item.displayName || null,
    dealerVerified: item.isVerified === true,
    dealerVerificationStatus: item.verificationStatus || null,
    dealerPublicState: item.publicState || null,
    city: item.city || null,
    department: item.department || null,
    activeInventoryCount: item.activeInventoryCount ?? null,
    contactAvailable: item.contactAvailable === true,
    position,
    isFeatured: item.isFeatured === true,
    sourcePage: DEALER_DIRECTORY_SOURCE_PAGE,
    sourceComponent,
    sourceAction,
    attributionContext: buildDealerDirectoryAttributionContext({
      sourceComponent,
      sourceAction,
      dealerSlug: item.slug || null,
    }),
    ...omitBlockedAnalyticsFields(extra),
  };

  BLOCKED_ITEM_FIELDS.forEach((key) => {
    delete payload[key];
  });

  return sanitizeAnalyticsPayload(payload);
}

export function buildDealerDirectoryFilterAnalyticsPayload({
  filterParams = null,
  sourceComponent = DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.FILTERS,
  sourceAction = null,
  changedFilterKey = null,
  removedFilterKey = null,
  previousSort = null,
  newSort = null,
  totalCount = null,
  filteredCount = null,
  extra = {},
} = {}) {
  const parsed = parseDealerDirectoryParams(filterParams);
  const activeFilterKeys = getActiveDealerDirectoryFilterKeys(parsed);

  return sanitizeAnalyticsPayload({
    sourcePage: DEALER_DIRECTORY_SOURCE_PAGE,
    sourceComponent,
    sourceAction,
    activeFilterKeys,
    activeFilterCount: activeFilterKeys.length,
    changedFilterKey,
    removedFilterKey,
    previousSort,
    newSort,
    sort: parsed.sort || null,
    totalCount,
    filteredCount,
    attributionContext: buildDealerDirectoryAttributionContext({
      sourceComponent,
      sourceAction,
    }),
    ...omitBlockedAnalyticsFields(extra),
  });
}

export function buildDealerDirectorySourcePayload({
  sourceComponent,
  sourceAction = null,
  destinationPath = null,
  visibleDealerCount = null,
  filteredDealerCount = null,
  extra = {},
} = {}) {
  const inboundSourcePage = extra.inboundSourcePage;
  return sanitizeAnalyticsPayload({
    sourcePage: DEALER_DIRECTORY_SOURCE_PAGE,
    sourceComponent,
    sourceAction,
    destinationPath,
    visibleDealerCount,
    filteredDealerCount,
    attributionContext: buildAttributionContext({
      sourcePage: inboundSourcePage || sourceComponent,
      sourceComponent,
      sourceAction,
      destinationPath,
    }),
    ...omitBlockedAnalyticsFields({ ...extra, inboundSourcePage: undefined }),
  });
}

export function trackDealerDirectoryAnalyticsEvent(eventName, payload = {}) {
  return trackDealerProfileEvent(eventName, payload);
}

export function trackDealerDirectoryViewed(counts = {}, filterParams = null) {
  return trackDealerDirectoryAnalyticsEvent(
    DEALER_DIRECTORY_ANALYTICS_EVENTS.VIEWED,
    buildDealerDirectoryAnalyticsPayload({
      sourceComponent: DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.PAGE,
      sourceAction: "page_view",
      visibleDealerCount: counts.visibleDealerCount,
      verifiedDealerCount: counts.verifiedDealerCount,
      withInventoryCount: counts.withInventoryCount,
      contactAvailableCount: counts.contactAvailableCount,
      filterParams,
    })
  );
}

export function trackDealerDirectoryEmptyViewed({
  emptyType = DEALER_DIRECTORY_EMPTY_TYPES.GLOBAL,
  counts = {},
  filterParams = null,
} = {}) {
  return trackDealerDirectoryAnalyticsEvent(
    DEALER_DIRECTORY_ANALYTICS_EVENTS.EMPTY_VIEWED,
    buildDealerDirectoryAnalyticsPayload({
      sourceComponent: DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.EMPTY_STATE,
      sourceAction: "empty_view",
      visibleDealerCount: counts.visibleDealerCount,
      filteredDealerCount: counts.filteredDealerCount,
      filterParams,
      extra: { emptyType },
    })
  );
}

export function trackDealerDirectoryErrorViewed(errorOrSource, extra = {}) {
  return trackDealerDirectoryAnalyticsEvent(
    DEALER_DIRECTORY_ANALYTICS_EVENTS.ERROR_VIEWED,
    buildDealerDirectoryAnalyticsPayload({
      sourceComponent: DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.ERROR_STATE,
      sourceAction: "error_view",
      extra: {
        errorCategory: mapDealerDirectoryErrorCategory(errorOrSource),
        ...extra,
      },
    })
  );
}

export function trackDealerDirectoryProfileClicked(item, options = {}) {
  const isFeatured = options.sourceComponent === DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.FEATURED;
  const eventName = isFeatured
    ? DEALER_DIRECTORY_ANALYTICS_EVENTS.FEATURED_DEALER_PROFILE_CLICKED
    : DEALER_DIRECTORY_ANALYTICS_EVENTS.PROFILE_CLICKED;

  const payload = buildDealerDirectoryItemAnalyticsPayload(item, {
    sourceComponent:
      options.sourceComponent || DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.CARD,
    sourceAction: options.sourceAction || "profile_click",
    position: options.position ?? null,
    extra: {
      filteredDealerCount: options.filteredDealerCount ?? null,
      totalCount: options.totalCount ?? null,
      sort: options.sort ?? null,
      activeFilterKeys: options.activeFilterKeys,
      activeFilterCount: options.activeFilterCount ?? null,
      featuredStrategy: options.featuredStrategy ?? null,
    },
  });

  return trackDealerDirectoryAnalyticsEvent(eventName, payload);
}

export function trackDealerDirectoryCardClicked(item, options = {}) {
  return trackDealerDirectoryAnalyticsEvent(
    DEALER_DIRECTORY_ANALYTICS_EVENTS.CARD_CLICKED,
    buildDealerDirectoryItemAnalyticsPayload(item, {
      sourceComponent:
        options.sourceComponent || DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.CARD,
      sourceAction: options.sourceAction || "card_click",
      position: options.position ?? null,
      extra: {
        filteredDealerCount: options.filteredDealerCount ?? null,
        totalCount: options.totalCount ?? null,
        sort: options.sort ?? null,
        activeFilterKeys: options.activeFilterKeys,
        activeFilterCount: options.activeFilterCount ?? null,
      },
    })
  );
}

export function trackDealerDirectoryFeaturedSectionViewed({ count, strategy } = {}) {
  return trackDealerDirectoryAnalyticsEvent(
    DEALER_DIRECTORY_ANALYTICS_EVENTS.FEATURED_SECTION_VIEWED,
    buildDealerDirectoryAnalyticsPayload({
      sourceComponent: DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.FEATURED,
      sourceAction: "section_view",
      extra: {
        count,
        featuredStrategy: strategy,
      },
    })
  );
}

export function trackDealerDirectorySearchCtaClicked({
  sourceComponent,
  destinationPath,
  counts = {},
} = {}) {
  return trackDealerDirectoryAnalyticsEvent(
    DEALER_DIRECTORY_ANALYTICS_EVENTS.SEARCH_CTA_CLICKED,
    buildDealerDirectorySourcePayload({
      sourceComponent,
      sourceAction: "search_cta_click",
      destinationPath,
      visibleDealerCount: counts.visibleDealerCount,
      filteredDealerCount: counts.filteredDealerCount,
    })
  );
}

export function trackDealerDirectoryChecklistClicked({ sourceComponent, destinationPath } = {}) {
  return trackDealerDirectoryAnalyticsEvent(
    DEALER_DIRECTORY_ANALYTICS_EVENTS.CHECKLIST_CLICKED,
    buildDealerDirectorySourcePayload({
      sourceComponent: sourceComponent || DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.TRUST_BLOCK,
      sourceAction: "checklist_click",
      destinationPath,
    })
  );
}

export function trackDealerDirectoryFilterDrawerOpened(filterParams = null) {
  const parsed = filterParams ? parseDealerDirectoryParams(filterParams) : null;
  const activeFilterKeys = parsed ? getActiveDealerDirectoryFilterKeys(parsed) : [];

  return trackDealerDirectoryAnalyticsEvent(
    DEALER_DIRECTORY_ANALYTICS_EVENTS.FILTER_DRAWER_OPENED,
    buildDealerDirectoryFilterAnalyticsPayload({
      filterParams: parsed,
      sourceComponent: DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.MOBILE_FILTERS,
      sourceAction: "drawer_open",
      extra: { activeFilterCount: activeFilterKeys.length },
    })
  );
}

export function trackDealerDirectoryFilterDrawerClosed(filterParams = null) {
  const parsed = filterParams ? parseDealerDirectoryParams(filterParams) : null;
  const activeFilterKeys = parsed ? getActiveDealerDirectoryFilterKeys(parsed) : [];

  return trackDealerDirectoryAnalyticsEvent(
    DEALER_DIRECTORY_ANALYTICS_EVENTS.FILTER_DRAWER_CLOSED,
    buildDealerDirectoryFilterAnalyticsPayload({
      filterParams: parsed,
      sourceComponent: DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.MOBILE_FILTERS,
      sourceAction: "drawer_close",
      extra: { activeFilterCount: activeFilterKeys.length },
    })
  );
}

const INBOUND_NAV_EVENTS = {
  [DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.HOMEPAGE]:
    DEALER_DIRECTORY_ANALYTICS_EVENTS.HOMEPAGE_CLICKED,
  [DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.SEARCH_RESULTS]:
    DEALER_DIRECTORY_ANALYTICS_EVENTS.SEARCH_RESULTS_CLICKED,
  [DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.VEHICLE_DETAIL]:
    DEALER_DIRECTORY_ANALYTICS_EVENTS.VEHICLE_DETAIL_CLICKED,
  [DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.DEALER_PROFILE]:
    DEALER_DIRECTORY_ANALYTICS_EVENTS.DEALER_PROFILE_CLICKED,
  [DEALER_DIRECTORY_ANALYTICS_SOURCE_COMPONENTS.FOOTER]:
    DEALER_DIRECTORY_ANALYTICS_EVENTS.FOOTER_CLICKED,
};

export function trackDealerDirectoryInboundNav(sourceComponent, extra = {}) {
  const eventName =
    INBOUND_NAV_EVENTS[sourceComponent] || DEALER_DIRECTORY_ANALYTICS_EVENTS.NAV_CLICKED;

  return trackDealerDirectoryAnalyticsEvent(
    eventName,
    buildDealerDirectorySourcePayload({
      sourceComponent,
      sourceAction: "inbound_nav_click",
      destinationPath: extra.destinationPath || "/concesionarios",
      extra,
    })
  );
}

export function createDealerDirectoryInboundNavHandler(sourceComponent, extra = {}) {
  return () => trackDealerDirectoryInboundNav(sourceComponent, extra);
}

export function countContactAvailableDealers(items = []) {
  return filterPublicDirectoryDealers(items).filter((item) => item.contactAvailable).length;
}
